// Hilos — the followed threads of the current team (shared/API.md §13; ios/Sources/Views/
// ThreadsView.swift). Local-first: rows render from the store on the first frame, one refresh rides
// the open, and switching tabs never touches the network. Opening a row marks it read on the tap.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import type { ThreadsSnapshot } from '../../../data/features/destinations';
import { ThreadsDerivation, type ThreadsMarkScope, type ThreadsTab } from '../../../core/threadsDerivation';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { Button, DestinationFrame, Dialog, EmptyState, InlineStatus, Segmented } from '../../kit/Kit';
import { useLive, useMinuteClock } from '../../hooks';
import { useShell } from '../../shell/ShellContext';
import { Icon } from '../../../design/icons';
import { applyOptimisticRead, buildThreadRows, isThreadUnread, replyCountLabel, type ThreadRow } from './destinationsModel';
import { PlaceLabel } from './PlaceLabel';
import './destinations.css';
import '../threads/threads.css';

type RefreshState = 'loading' | 'idle' | 'failed';

/**
 * Hilos as a SHEET over the conversation, like the Mac's (`MacChannelSidebar` `.sheet`, ~520×480): Esc,
 * the scrim or Close return to the conversation; a row opens its thread beside its conversation and the
 * sheet goes (user decision 2026-10-12 — it used to replace the conversation column).
 */
export function ThreadsSheet({ focusRootId, onClose }: { focusRootId: string | null; onClose(): void }) {
  return (
    <Dialog title={t('threads.title')} onClose={onClose} width={560}>
      <ThreadsView focusRootId={focusRootId} inSheet />
    </Dialog>
  );
}

export function ThreadsView({ focusRootId, inSheet = false }: { focusRootId: string | null; inSheet?: boolean }) {
  const shell = useShell();
  const teamId = shell.team.id;
  const now = useMinuteClock();
  // UNREADS first (user direction 2026-08-31): the inbox opens on what needs attention.
  const [tab, setTab] = useState<ThreadsTab>('unreads');
  const [refresh, setRefresh] = useState<RefreshState>('loading');
  const [attempt, setAttempt] = useState(0);
  const { value: snapshot } = useLive(() => data.call('threadsSnapshot', teamId), ['threads', 'threadParticipants', 'posts', 'users', 'channels', 'channelParticipants', 'syncState', 'rootReplyStats', 'channelMembers', 'threadSeen'], [teamId]);

  // The optimistic read state, tied to the snapshot it was made against (see applyOptimisticRead).
  const [optimistic, setOptimistic] = useState<{ base: ThreadsSnapshot | undefined; all: boolean; ids: Set<string> }>({ base: undefined, all: false, ids: new Set() });

  useEffect(() => {
    let alive = true;
    setRefresh('loading');
    data.call('refreshThreads', teamId).then(
      (r) => alive && setRefresh(r.ok ? 'idle' : 'failed'),
      () => alive && setRefresh('failed'),
    );
    return () => {
      alive = false;
    };
  }, [teamId, attempt]);

  const allRows = useMemo(() => (snapshot ? buildThreadRows(snapshot, now) : []), [snapshot, now]);
  const rows = useMemo(
    () => (optimistic.base === snapshot ? applyOptimisticRead(allRows, optimistic) : allRows),
    [allRows, optimistic, snapshot],
  );
  const visible = ThreadsDerivation.visibleThreads(rows, tab) as ThreadRow[];
  const unreadCount = rows.filter(isThreadUnread).length;

  const markRead = useCallback(
    (id: string) => {
      setOptimistic((o) => {
        const ids = new Set(o.base === snapshot ? o.ids : []);
        ids.add(id);
        return { base: snapshot, all: o.base === snapshot && o.all, ids };
      });
      void data.call('markThreadRead', teamId, id);
    },
    [snapshot, teamId],
  );

  const open = (row: ThreadRow) => {
    if (isThreadUnread(row)) markRead(row.id);
    if (inSheet) shell.openThreadInConversation(row.channelId, row.id);
    else shell.openThread(row.id);
  };

  // /<team>/threads/<rootId>: open that thread beside the list on arrival — once per id, so a
  // workspace that answers openThread with the same URL cannot loop.
  const focused = useRef<string | null>(null);
  useEffect(() => {
    if (!focusRootId || focused.current === focusRootId) return;
    focused.current = focusRootId;
    const row = allRows.find((r) => r.id === focusRootId);
    if (row && isThreadUnread(row)) markRead(row.id);
    shell.openThread(focusRootId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRootId]);

  // Follow / «Dejar de seguir» from the list (P165, §13.8): the worker moves the row at once and the
  // threads refetch brings the list in line; a refusal says so.
  const setFollowing = async (id: string, follow: boolean) => {
    const ok = await data.call('setThreadFollowing', teamId, id, follow).catch(() => false);
    if (!ok) shell.toast(t('web.thread.followFailed'), 'error');
  };

  // «Marcar como leído» (§13.8): followed threads go to the server (§13.2), the ones the reader does
  // not follow only get the device's own mark. The rows clear on the click.
  const markScope = (scope: ThreadsMarkScope) => {
    const others = rows.filter((r) => !r.isFollowing).map((r) => r.id);
    const followedIds = rows.filter((r) => r.isFollowing).map((r) => r.id);
    setOptimistic({ base: snapshot, all: scope === 'all', ids: new Set(scope === 'following' ? followedIds : others) });
    if (scope !== 'notFollowing') void data.call('markAllThreadsRead', teamId);
    if (scope !== 'following') void data.call('markUnfollowedThreadsSeen', others);
  };

  // Older pages: "Todos" only — unreads are complete by construction.
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  // The server's cursor is a FOLLOWED thread id (§13.1); the merged list may end on a derived one.
  const oldestId = [...visible].reverse().find((r) => r.isFollowing)?.id ?? null;
  const canPage = (tab === 'following' || tab === 'all') && hasMoreOlder && !!oldestId && refresh !== 'loading' && allRows.length >= 60;
  useEffect(() => {
    setHasMoreOlder(true);
  }, [teamId]);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !canPage || loadingOlder) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting) || !oldestId) return;
      observer.disconnect();
      setLoadingOlder(true);
      data.call('loadOlderThreads', teamId, oldestId).then(
        (more) => {
          setHasMoreOlder(more);
          setLoadingOlder(false);
        },
        () => setLoadingOlder(false),
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [canPage, loadingOlder, oldestId, teamId]);

  const unavailable = snapshot?.available === false;
  const neverFetched = !snapshot || snapshot.fetchedAt === 0;

  let body;
  if (unavailable) {
    body = <EmptyState title={t('web.destinations.threads.unavailable.title')} body={t('web.destinations.threads.unavailable.body')} />;
  } else if (!visible.length) {
    if (neverFetched && refresh === 'loading') body = <InlineStatus kind="loading" text={t('web.destinations.loading')} />;
    else if (neverFetched && refresh === 'failed') body = <InlineStatus kind="error" text={t('web.destinations.threads.refreshFailed')} onRetry={() => setAttempt((n) => n + 1)} />;
    else
      body = (
        <EmptyState
          title={tab === 'unreads' ? t('threads.empty.unreads') : tab === 'notFollowing' ? t('threads.empty.notFollowing') : t('threads.empty')}
        />
      );
  } else {
    body = (
      <>
        {refresh === 'failed' ? <InlineStatus kind="error" text={t('web.destinations.threads.refreshFailed')} onRetry={() => setAttempt((n) => n + 1)} /> : null}
        <div className="dest-list" role="list">
          {visible.map((row) => (
            <ThreadRowView key={row.id} row={row} active={row.id === focusRootId} onOpen={() => open(row)} onToggleFollow={() => void setFollowing(row.id, !row.isFollowing)} />
          ))}
        </div>
        {canPage ? <div ref={sentinel} className="dest-sentinel" /> : null}
        {loadingOlder ? <InlineStatus kind="loading" text={t('web.destinations.threads.loadingOlder')} /> : null}
      </>
    );
  }

  const scopes = ThreadsDerivation.markScopes(rows);
  const markAllAction = scopes.length && !unavailable ? <MarkReadButton scopes={scopes} onPick={markScope} /> : null;
  const tabs = unavailable ? null : (
    <Segmented<ThreadsTab>
      value={tab}
      onChange={setTab}
      options={[
        { value: 'unreads', label: t('threads.tab.unreads'), count: unreadCount },
        { value: 'all', label: t('threads.tab.all') },
        { value: 'following', label: t('threads.tab.following') },
        { value: 'notFollowing', label: t('threads.tab.notFollowing') },
      ]}
    />
  );
  if (inSheet) {
    return (
      <div className="threads-sheet-root">
        <div className="threads-sheet-toolbar">
          {tabs}
          {markAllAction}
        </div>
        <div className="threads-sheet-body">{body}</div>
      </div>
    );
  }

  return (
    <DestinationFrame icon="threads" title={t('threads.title')} subtitle={shell.team.displayName} actions={markAllAction} toolbar={tabs}>
      {body}
    </DestinationFrame>
  );
}

const SCOPE_LABEL: Record<ThreadsMarkScope, string> = {
  all: 'threads.markScope.all',
  following: 'threads.markScope.following',
  notFollowing: 'threads.markScope.notFollowing',
};

/** «Marcar como leído» → a menu of scopes (§13.8): all / followed / not followed. */
function MarkReadButton({ scopes, onPick }: { scopes: ThreadsMarkScope[]; onPick(scope: ThreadsMarkScope): void }) {
  const anchor = useRef<HTMLSpanElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !anchor.current || !menu.current) return;
    const a = anchor.current.getBoundingClientRect();
    const r = menu.current.getBoundingClientRect();
    setPos({ left: Math.max(8, Math.min(a.right - r.width, window.innerWidth - r.width - 8)), top: a.bottom + 6 });
  }, [open]);
  useEffect(() => {
    if (!open) return;
    // Click-outside spares the opener, or its own click would close and reopen the menu.
    const close = (e: MouseEvent) => !menu.current?.contains(e.target as Node) && !anchor.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', esc, true);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc, true);
    };
  }, [open]);
  return (
    <span ref={anchor} className="dest-action" title={t('threads.markRead')}>
      <Button kind="quiet" icon="checkDouble" onClick={() => setOpen((o) => !o)}>
        <span className="dest-action-label">{t('threads.markRead')}</span>
      </Button>
      {open
        ? createPortal(
            <div ref={menu} className="popover" role="menu" data-threads-mark-menu style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}>
              {scopes.map((scope) => (
                <button
                  key={scope}
                  type="button"
                  className="menu-item"
                  role="menuitem"
                  data-mark-scope={scope}
                  onClick={() => {
                    setOpen(false);
                    onPick(scope);
                  }}
                >
                  {t(SCOPE_LABEL[scope])}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

function ThreadRowView({ row, active, onOpen, onToggleFollow }: { row: ThreadRow; active: boolean; onOpen(): void; onToggleFollow(): void }) {
  const unread = isThreadUnread(row);
  return (
    <div
      role="listitem"
      tabIndex={0}
      className={`dest-row thread-row${unread ? ' unread' : ''}${active ? ' active' : ''}`}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? (e.preventDefault(), onOpen()) : undefined)}
    >
      <div className="thread-faces" aria-hidden="true">
        {row.avatarUserIds.map((id) => (
          <Avatar key={id} userId={id} name={row.avatarNames[id] ?? '?'} size={28} version={row.avatarVersions[id] ?? 0} />
        ))}
      </div>
      <div className="dest-row-main">
        <div className="dest-row-head">
          <PlaceLabel name={row.channelName} type={row.placeType} />
          {row.isUrgent ? <span className="priority-pill urgent">{t('channel.priority.urgent')}</span> : null}
          <span className="dest-time">{row.timeLabel}</span>
          <button
            type="button"
            className="icon-button thread-unfollow"
            title={row.isFollowing ? t('web.thread.unfollowFromList') : t('threads.follow')}
            aria-label={row.isFollowing ? t('web.thread.unfollowFromList') : t('threads.follow')}
            data-thread-unfollow={row.isFollowing ? row.id : undefined}
            data-thread-follow={row.isFollowing ? undefined : row.id}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFollow();
            }}
          >
            <Icon name={row.isFollowing ? 'bellSlash' : 'bell'} size={14} />
          </button>
        </div>
        <div className="thread-root">
          <strong>{row.rootAuthorName}</strong>
          <span>{row.rootText}</span>
        </div>
        {row.replyPreview ? <div className="thread-reply">{row.replyPreview}</div> : null}
        <div className="thread-meta">
          <span className="thread-count">
            <Icon name="reply" size={12} />
            {replyCountLabel(row.replyCount)}
          </span>
          <span className="thread-people">{row.participantSummary}</span>
          {/* Which kind of thread this is (§13.8) — why it is or is not in each tab. */}
          <span className={`thread-follow-tag${row.isFollowing ? ' following' : ''}`}>
            {row.isFollowing ? t('threads.tag.following') : t('threads.tag.notFollowing')}
          </span>
          <span className="thread-state">
            {row.unreadMentions > 0 ? (
              <span className="badge">{row.unreadMentions}</span>
            ) : null}
            {row.unreadReplies > 0 ? <span className="thread-new">{tf('threads.newReplies', row.unreadReplies)}</span> : unread ? <span className="unread-dot-row" /> : null}
          </span>
        </div>
      </div>
    </div>
  );
}
